import { useContext, useEffect } from 'react'
import { Link, Link as RouterLink, useLocation, useNavigate } from 'react-router-dom'
// material
import { Box, Divider, Drawer, Stack } from '@mui/material'
import { alpha } from '@mui/material/styles'
// hooks
import useCollapseDrawer from '../../hooks/useCollapseDrawer'

// components
import { ReactComponent as MyGuarantee } from '../../assets/icons/myguarantee.svg'

import NavSection, { Footer } from '../../components/NavSection'
//
import { Icon } from '@iconify/react'
import { styled } from '@mui/material/styles'
import Cookies from 'js-cookie'
import AccountExpanded from 'layouts/common/AccountExpanded'
import { useSelector } from 'react-redux'
import { RootState } from 'redux/store'
import FadeMenu from './CreateButtonNavigation'
import sidebarConfig, { adminSettings, settingsConfig, termsAndConditions } from './SidebarConfig'
import { RootStyle } from './styles/index.b'
// ----------------------------------------------------------------------
import { GlobalInfoContext, initialState } from 'contexts/GlobalStateContext'

import { useAuth } from 'hooks/useAuth'
import { useSocketConnection  } from 'hooks/useSocketConnection';
import { useSnackbar } from 'notistack'
//import { useNavigate } from 'react-router'
import { UIContractState } from '@types'
import { clearAllReduxCaches } from 'pages/dashboard/utils'
import { dispatch } from 'redux/store'
import { PRIMARY, SECONDARY } from 'theme/palette'
import CreatePaperGuarantee from './CreatePaperGuaranteeNavigation'

const DRAWER_WIDTH = 300
const COLLAPSE_WIDTH = 110

type DashboardSidebarProps = {
  isOpenSidebar: boolean
  onCloseSidebar: VoidFunction
  noOfTasks: number
  noOfNotifications: number
  noOfOrgTasks: number
}

const Container = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-end',
  height: '100%',
  color: '#000000',
  /*@ts-ignore*/
  backgroundColor: window?.brand === 'nab' ? '#000000' : SECONDARY.main
})

const NavItem = styled('div')`
  padding: 10px 0 10px 32px;
  cursor: pointer;

  & a {
    display: flex;
    color: #000000;
    font-size: 14px;
    text-decoration: none;
    & svg {
      margin-right: 15px;
    }
  }
  &:hover {
    background-color: rgba(145, 158, 171, 0.08);
  }
`

const getLogo = (user) => {
  const role = user?.roles
  switch (role) {
    case 'admin':
      return <MyGuarantee />
    case 'issuer':
      return null
    case 'applicant':
      return null
    case 'beneficiary':
      return null
    default:
      return (
        <div style={{ paddingLeft: '15px' }}>
          <MyGuarantee />
        </div>
      )
  }
}

export default function DashboardSidebar({
  isOpenSidebar,
  onCloseSidebar,
  noOfTasks,
  noOfNotifications,
  noOfOrgTasks
}: DashboardSidebarProps) {
  let bgColor = PRIMARY.newBg
  /*@ts-ignore*/
  if (window?.brand === 'nab') {
    bgColor = '#000000'
  }
  const { pathname } = useLocation()
  const { user } = useSelector((state: RootState) => state.userReducer)
  const { businessUnitActionContexts: buacs } = useSelector(
    (state: RootState) => state.createGuaranteePayload
  )
  const { adminActions } = useSelector((state: RootState) => state.adminCreateActions)
  const sideConfig = sidebarConfig(noOfTasks, noOfNotifications, noOfOrgTasks)
  const { isCollapse, collapseHover, onHoverEnter, onHoverLeave } = useCollapseDrawer()

  const navigate = useNavigate()
  const { logout } = useAuth()
  const { enqueueSnackbar } = useSnackbar()
  const { onSocketClosed } = useSocketConnection()

  const globalContext = useContext(GlobalInfoContext)

  const handleLogout = async () => {
    try {
      Cookies.remove('token')
      globalContext.setReqInfo(initialState)
      clearAllReduxCaches(dispatch)

      onSocketClosed()

      await logout?.()
      navigate('/auth/login')
      // window.location.href = `${cognitoConfig.RE}/logout?client_id=${cognitoConfig.userPoolClientId}&logout_uri=${cognitoConfig.logoutUri}&redirect_uri=${cognitoConfig.redirectUri}&response_type=${cognitoConfig.oauthResponseType}`
      window.history.replaceState(null, '', '/')
    } catch (error) {
      console.error(error)
      enqueueSnackbar('Unable to logout', { variant: 'error' })
    }
  }

  useEffect(() => {
    if (isOpenSidebar) {
      onCloseSidebar()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])
  // Ensure `buacs` is loaded before rendering `FadeMenu`
  const shouldShowFadeMenu = canCreateByType(buacs, 'TriContract')
  const isAdmin = adminActions?.length > 0
  const renderContent = (
    <Box
      sx={{
        height: 1,
        backgroundColor: bgColor,
        color: '#000000',
        '& a': {
          fontSize: '14px',
          fontWeight: 'bold',
          color: '#000000',
          textDecoration: 'none'
        },
        overflow: 'hidden'
      }}
      key="scroll-bar"
    >
      <div>
        <Stack
          spacing={3}
          sx={{
            px: 2.5,
            pt: 3,
            pb: 2,
            ...(isCollapse && {
              alignItems: 'center'
            })
          }}
        >
          <Stack key="stack-nav" direction="row" alignItems="center" justifyContent="space-between">
            <Box
              key="stack-box"
              component={RouterLink}
              to="/dashboard/app"
              sx={{ display: 'inline-flex' }}
              aria-labelledby="arc-gx-logo"
            >
              {getLogo(user)}
            </Box>
          </Stack>
        </Stack>

        <AccountExpanded user={user} />
        <NavSection
          key="nav-section-desktop"
          navConfig={sideConfig}
          isShow={true}
          className="sidebar"
        />
        {shouldShowFadeMenu && <FadeMenu />}
        {buacs.length > 0 && canCreateByType(buacs, 'TriContractPaperRecord') && (
          <CreatePaperGuarantee />
        )}
        {isAdmin && (
          <NavItem>
            <Link to="/dashboard/admin" data-testid="admin">
              <Icon icon="mdi:settings" width={24} height={24} color="#000" />
              Admin
            </Link>
          </NavItem>
        )}
        <Divider sx={{ mt: 3 }} />
        <NavSection
          key="nav-section-desktop-settings"
          navConfig={settingsConfig}
          isShow={true}
          className="setting-sidebar"
        />
        <NavItem>
          <Link to="#" onClick={handleLogout} data-testid="logout">
            <Icon icon="mdi-light:logout" width={24} height={24} color="#000000" />
            Log Out
          </Link>
        </NavItem>
      </div>

      <Stack
        key="stack-nav"
        direction="column"
        justifyContent="space-between"
        sx={{ margin: '60px 0 60px 26px' }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            margin: '5px'
          }}
        >
          <Icon
            icon="fluent:chat-help-24-regular"
            width={24}
            height={24}
            style={{ marginRight: '10px' }}
            color="#000000"
          />
          <a href="mailto:support@myguarantee.com.au">support@myguarantee.com.au</a>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            margin: '5px'
          }}
        >
          <Icon
            icon="solar:phone-outline"
            width={24}
            height={24}
            style={{ marginRight: '10px' }}
            color="#000000"
          />
          <a href="tel:1300 156 703">1300 156 703</a>
        </Box>
      </Stack>

      <Container>
        <div>
          <Footer items={termsAndConditions} />
        </div>
      </Container>
    </Box>
  )

  return (
    <RootStyle
      sx={{
        width: {
          lg: DRAWER_WIDTH,
          zIndex: 1
        }
      }}
    >
      <div>
        <Drawer
          open
          variant="persistent"
          onMouseEnter={onHoverEnter}
          onMouseLeave={onHoverLeave}
          PaperProps={{
            sx: {
              width: DRAWER_WIDTH,
              bgcolor: 'background.default',
              ...(isCollapse && {
                width: COLLAPSE_WIDTH
              }),
              ...(collapseHover && {
                borderRight: 0,
                backdropFilter: 'blur(6px)',
                WebkitBackdropFilter: 'blur(6px)',
                bgcolor: (theme) => alpha(theme.palette.background.default, 0.88)
              })
            }
          }}
        >
          {renderContent}
        </Drawer>
      </div>
    </RootStyle>
  )
}

const canCreateByType = (buacs: Array<UIContractState>, type: string) =>
  buacs.length > 0 && buacs.some((buac) => buac?.contractType === type)
